import { ClipboardModule } from '@angular/cdk/clipboard';
import { CdkDropList, DragDropModule } from '@angular/cdk/drag-drop';
import { CdkMenuModule } from '@angular/cdk/menu';
import { CdkPortalOutlet } from '@angular/cdk/portal';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { DatePipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import localeNL from '@angular/common/locales/nl';
import { APP_INITIALIZER, ErrorHandler, Injectable, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChip, MatChipSet } from '@angular/material/chips';
import {
    DateAdapter,
    MAT_DATE_LOCALE,
    MatNativeDateModule,
    MatRippleModule,
    NativeDateAdapter,
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions, MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule, HAMMER_GESTURE_CONFIG, HammerGestureConfig, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgSelectModule } from '@ng-select/ng-select';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { PlanningHistoryDialogComponent } from 'src/app/trackline/pages/planning-history-dialog/planning-history-dialog.component';
import { TracklinePlanningFilesComponent } from 'src/app/trackline/pages/trackline-planning-edit-dialog/trackline-planning-files/trackline-planning-files.component';
import { ActivityLetterPipe } from './activity-letter.pipe';
import { AfasFunctionsManagementComponent } from './afas-functions-management/afas-functions-management.component';
import { FunctionHasRolePipe } from './afas-functions-management/function-has-role.pipe';
import { IsRoleDisabledPipe } from './afas-functions-management/is-role-disabled.pipe';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CanDeactivateForm } from './can-deactivate-form';
import { CopyStaffFromOtherDayComponent } from './check-hours/copy-staff-from-other-day/copy-staff-from-other-day.component';
import { MoveRealisationsToPerformerDialogComponent } from './check-hours/move-realisations-to-performer-dialog/move-realisations-to-performer-dialog.component';
import { CodaltComponent } from './codalt.component';
import { CodaltInterceptor } from './codalt.interceptor';
import { CodaltUploadImageComponent } from './components/codalt-upload-image/codalt-upload-image.component';
import { DatetimeInputComponent } from './components/datetime-input.component';
import { FieldInputModule } from './components/field-input/field-input.module';
import { FieldsTemplateModule } from './components/fields-template/fields-template.module';
import { LocationSelectComponent } from './components/location-select/location-select.component';
import { MinutesInputComponent } from './components/minutes-input.component';
import { PlanningTableComponent } from './components/planning-table/planning-table.component';
import { SelectItemDialogComponent } from './components/select-item-dialog/select-item-dialog.component';
import { SelectProjectDialogComponent } from './components/select-project-dialog/select-project-dialog.component';
import { CustomErrorHandlerService } from './custom-error-handler-service';
import { DatepickerWeekNumberDirective } from './directives/datepicker-week-number.directive';
import { DuplicateEntrySelectDialogComponent } from './duplicate-entry-select-dialog/duplicate-entry-select-dialog.component';
import { IsAsphaltEntryPipe } from './duplicate-entry-select-dialog/is-asphalt-entry.pipe';
import { ExecutorProjectChoiceDialogComponent } from './executor-project-choice-dialog/executor-project-choice-dialog.component';
import { TinyInsertImageComponent } from './generic/components/simple-tiny/tiny-insert-image/tiny-insert-image.component';
import { GenericModule } from './generic/generic.module';
import { HasLicensePlatePipe } from './has-license-plate.pipe';
import { IsNightPipe } from './is-night.pipe';
import { IsSubsequentPipe } from './is-subsequent.pipe';
import { LicensePlatePipe } from './license-plate.pipe';
import { MainPlanningPipe } from './main-planning.pipe';
import { MaterialPiecePipe } from './material-piece.pipe';
import { MultipleDriverPipe } from './multiple-driver.pipe';
import { MultiplePipe } from './multiple.pipe';
import { Nl2brPipe } from './nl2br.pipe';
import { AiReportsModule } from './pages/ai-reports/ai-reports.module';
import { AsphaltExportDialogComponent } from './pages/asphalt-export-dialog/asphalt-export-dialog.component';
import { AsphaltOrderListComponent } from './pages/asphalt-order-list/asphalt-order-list.component';
import { AsphaltComponent } from './pages/asphalt/asphalt.component';
import { ImportAsphaltListDialogComponent } from './pages/asphalt/import-asphalt-list-dialog/import-asphalt-list-dialog.component';
import { LinkLoginComponent } from './pages/auth/link-login/link-login.component';
import { MicrosoftCallbackComponent } from './pages/auth/microsoft-callback/microsoft-callback.component';
import { RequestRestorePasswordComponent } from './pages/auth/request-restore-password/request-restore-password.component';
import { RestorePasswordComponent } from './pages/auth/restore-password/restore-password.component';
import { AddRealisationDialogComponent } from './pages/check-hours/add-realisation-dialog/add-realisation-dialog.component';
import { AllApprovedPipe } from './pages/check-hours/all-approved.pipe';
import { CheckHoursFilterAnyPipe } from './pages/check-hours/check-hours-filter-any.pipe';
import { CheckHoursFilterPipe } from './pages/check-hours/check-hours-filter.pipe';
import { CheckHoursComponent } from './pages/check-hours/check-hours.component';
import { CopyRealisationFromOtherDayComponent } from './pages/check-hours/copy-realisation-from-other-day/copy-realisation-from-other-day.component';
import { DoesNotMatchDriverPipe } from './pages/check-hours/does-not-match-driver.pipe';
import { EditRealisationProjectDialogComponent } from './pages/check-hours/edit-realisation-project-dialog/edit-realisation-project-dialog.component';
import { IsTraveltimePipe } from './pages/check-hours/is-traveltime.pipe';
import { IsWorktimePipe } from './pages/check-hours/is-worktime.pipe';
import { NotApprovablePipe } from './pages/check-hours/not-approvable.pipe';
import { NotEditablePipe } from './pages/check-hours/not-editable.pipe';
import { DayCheckRealisationsComponent } from './pages/day-check-realisations/day-check-realisations.component';
import { DayReportModule } from './pages/day-report/day-report.module';
import { AddLeaveNameDialogComponent } from './pages/employee-planning/add-leave-name-dialog/add-leave-name-dialog.component';
import { AddProjectDialogComponent } from './pages/employee-planning/add-project-dialog/add-project-dialog.component';
import { EmployeeDayInfoDialogComponent } from './pages/employee-planning/employee-day-info-dialog/employee-day-info-dialog.component';
import { EmployeeIsNightPipe } from './pages/employee-planning/employee-is-night.pipe';
import { EmployeePlanningHelpComponent } from './pages/employee-planning/employee-planning-help/employee-planning-help.component';
import { EmployeePlanningMakeKeyPipe } from './pages/employee-planning/employee-planning-make-key.pipe';
import { EmployeePlanningUserRowComponent } from './pages/employee-planning/employee-planning-user-row/employee-planning-user-row.component';
import { EmployeePlanningComponent } from './pages/employee-planning/employee-planning.component';
import { EntityIsNightPipe } from './pages/employee-planning/entity-is-night.pipe';
import { LeaveRepeatDaysPipe } from './pages/employee-planning/leave-repeat-days.pipe';
import { ProjectDayHasAnyTeamPipe } from './pages/employee-planning/project-day-has-any-team.pipe';
import { EntitiesOverviewComponent } from './pages/entities-overview/entities-overview.component';
import { EntityDetailComponent } from './pages/entities-overview/entity-detail/entity-detail.component';
import { HirePeopleDialogComponent } from './pages/hire-people-dialog/hire-people-dialog.component';
import { HiredCompaniesDialogComponent } from './pages/hired-companies-dialog/hired-companies-dialog.component';
import { HiringCompaniesDialogComponent } from './pages/hiring-companies-dialog/hiring-companies-dialog.component';
import { HiringCompanyEditDialogComponent } from './pages/hiring-companies-dialog/hiring-company-edit-dialog/hiring-company-edit-dialog.component';
import { CommentDialogComponent } from './pages/hour-registration/comment-dialog/comment-dialog.component';
import { HistoryDialogComponent } from './pages/hour-registration/history-dialog/history-dialog.component';
import { OwnHourEditAllowedPipe } from './pages/hour-registration/hour-edit-allowed.pipe';
import { HourEditDialogComponent } from './pages/hour-registration/hour-edit-dialog/hour-edit-dialog.component';
import { HourEditTimeframePipe } from './pages/hour-registration/hour-edit-timeframe.pipe';
import { HourRegistrationComponent } from './pages/hour-registration/hour-registration.component';
import { ProjectSelectionDialogComponent } from './pages/hour-registration/project-selection-dialog/project-selection-dialog.component';
import { ExcludedKeyPipe } from './pages/leave/leave-edit/excluded-key.pipe';
import { LeaveEditComponent } from './pages/leave/leave-edit/leave-edit.component';
import { LeaveComponent } from './pages/leave/leave.component';
import { LogoutComponent } from './pages/logout/logout.component';
import { MaterialsManagement } from './pages/materials-management/materials-management.component';
import { EditSettlementProjectDialogComponent } from './pages/not-send-entities/edit-settlement-project-dialog/edit-settlement-project-dialog.component';
import { NotSendSettlementsComponent } from './pages/not-send-entities/not-send-settlements.component';
import { PastToolboxesDialogComponent } from './pages/past-toolboxes-dialog/past-toolboxes-dialog.component';
import { PdfBackgroundDialogComponent } from './pages/pdf-background-dialog/pdf-background-dialog.component';
import { CumulativeSettlementAmountPipe } from './pages/period-settlement/cumulative-settlement-amount.pipe';
import { CumulativeSettlementMinutesPipe } from './pages/period-settlement/cumulative-settlement-minutes.pipe';
import { PeriodSettlementComponent } from './pages/period-settlement/period-settlement.component';
import { RealisationForSettlementPipe } from './pages/period-settlement/realisation-for-settlement.pipe';
import { PlanningDetailPrintComponent } from './pages/planning-detail-print/planning-detail-print.component';
import { PlanningExportDialogComponent } from './pages/planning-export-dialog/planning-export-dialog.component';
import { PlanningListComponent } from './pages/planning-list/planning-list.component';
import { DayComponent } from './pages/planning-overview/day/day.component';
import { EntityComponent } from './pages/planning-overview/day/entity/entity.component';
import { TonsComponent } from './pages/planning-overview/day/tons/tons.component';
import { TrucksComponent } from './pages/planning-overview/day/trucks/trucks.component';
import { PlanningDetailContainerComponent } from './pages/planning-overview/planning-detail-container/planning-detail-container.component';
import { AsphaltListItemComponent } from './pages/planning-overview/planning-detail/asphalt-list/asphalt-list-item/asphalt-list-item.component';
import { AsphaltListComponent } from './pages/planning-overview/planning-detail/asphalt-list/asphalt-list.component';
import { CutterListComponent } from './pages/planning-overview/planning-detail/cutter-list/cutter-list.component';
import { HirePeopleSelectorDialogComponent } from './pages/planning-overview/planning-detail/hire-people-selector-dialog/hire-people-selector-dialog.component';
import { PlanningDetailDialogComponent } from './pages/planning-overview/planning-detail/planning-detail-dialog.component';
import { WipetruckListComponent } from './pages/planning-overview/planning-detail/wipetruck-list/wipetruck-list.component';
import { PlanningOverviewComponent } from './pages/planning-overview/planning-overview.component';
import { SetPlanningFixedDialogComponent } from './pages/planning-overview/set-planning-fixed-dialog/set-planning-fixed-dialog.component';
import { ProfileAfasComponent } from './pages/profile/profile-afas/profile-afas.component';
import { ProfileComponent } from './pages/profile/profile.component';
import { AsphaltUsedListItemComponent } from './pages/project-day-report/asphalt-used-list/asphalt-list-item/asphalt-used-list-item.component';
import { AsphaltUsedListComponent } from './pages/project-day-report/asphalt-used-list/asphalt-used-list.component';
import { ProjectDayReportComponent } from './pages/project-day-report/project-day-report.component';
import { ProjectDetailDialogComponent } from './pages/project-detail-dialog/project-detail-dialog.component';
import { PmNamePipe } from './pages/project-executor-planning/pm-name.pipe';
import { ProjectExecutorPlanningComponent } from './pages/project-executor-planning/project-executor-planning.component';
import { ProjectOverviewComponent } from './pages/project-overview/project-overview.component';
import { ProjectWeekReportConfigComponent } from './pages/project-week-report-dialog/project-week-report-config.component';
import { EditWeekCommentDialogComponent } from './pages/project-week-report-dialog/project-week-report/edit-week-comment-dialog/edit-week-comment-dialog.component';
import { GroupDayChangesPipe } from './pages/project-week-report-dialog/project-week-report/project-week-report-hours/group-day-changes.pipe';
import { ProjectWeekReportHoursComponent } from './pages/project-week-report-dialog/project-week-report/project-week-report-hours/project-week-report-hours.component';
import { ProjectWeekReportComponent } from './pages/project-week-report-dialog/project-week-report/project-week-report.component';
import { WeekCommentHistoryDialogComponent } from './pages/project-week-report-dialog/project-week-report/week-comment-history-dialog/week-comment-history-dialog.component';
import { WeekreportPdfOptionsDialogComponent } from './pages/project-week-report-dialog/weekreport-pdf-options-dialog/weekreport-pdf-options-dialog.component';
import { ProjectmanagerReportDialogComponent } from './pages/projectmanager-report-dialog/projectmanager-report-dialog.component';
import { ProjectEditDialogComponent } from './pages/projects/project-edit-dialog/project-edit-dialog.component';
import { ProjectStatusPipe } from './pages/projects/project-status.pipe';
import { ProjectsComponent } from './pages/projects/projects.component';
import { FunctionalityHasFeaturePipe } from './pages/rights-management/functionality-has-feature.pipe';
import { RightsManagementComponent } from './pages/rights-management/rights-management.component';
import { RoleHasFunctionalityPipe } from './pages/rights-management/role-has-functionality.pipe';
import { SettlementSendAllComponent } from './pages/settlement-send-all/settlement-send-all.component';
import { SettlementComponent } from './pages/settlement/settlement.component';
import { EditSettlementDialogComponent } from './pages/settlements-send/edit-settlement-dialog/edit-settlement-dialog.component';
import { SettlementsSendComponent } from './pages/settlements-send/settlements-send.component';
import { StaffingComponent } from './pages/staffing/staffing.component';
import { ToolboxReadDialogComponent } from './pages/toolbox-read-dialog/toolbox-read-dialog.component';
import { QuestionComponent } from './pages/toolbox/question/question.component';
import { QuizStatsComponent } from './pages/toolbox/quiz-stats/quiz-stats.component';
import { QuizComponent } from './pages/toolbox/quiz/quiz.component';
import { ToolboxDialogComponent } from './pages/toolbox/toolbox-dialog/toolbox-dialog.component';
import { ToolboxEditComponent } from './pages/toolbox/toolbox-edit/toolbox-edit.component';
import { ToolboxQuestionFormComponent } from './pages/toolbox/toolbox-question-form/toolbox-question-form.component';
import { ToolboxQuizStatsComponent } from './pages/toolbox/toolbox-quiz-stats/toolbox-quiz-stats.component';
import { ToolboxComponent } from './pages/toolbox/toolbox.component';
import { EquipmentDialogComponent } from './pages/transport-planning/equipment-dialog/equipment-dialog.component';
import { HireNameComponent } from './pages/transport-planning/hire-name/hire-name.component';
import { AddressDialogComponent } from './pages/transport-planning/planning-detail-employees/address-dialog/address-dialog.component';
import { AddressIconComponent } from './pages/transport-planning/planning-detail-employees/address-icon/address-icon.component';
import { PlanningDetailEmployeesComponent } from './pages/transport-planning/planning-detail-employees/planning-detail-employees.component';
import { TransportAddressComponent } from './pages/transport-planning/transport-address/transport-address.component';
import { TransportInUseComponent } from './pages/transport-planning/transport-in-use/transport-in-use.component';
import { TransportPlanningRowComponent } from './pages/transport-planning/transport-planning-row/transport-planning-row.component';
import { SimpleTransportTinyComponent } from './pages/transport-planning/transport-planning-text/simple-transport-tiny/simple-transport-tiny.component';
import { TransportPlanningTextComponent } from './pages/transport-planning/transport-planning-text/transport-planning-text.component';
import { TransportPlanningComponent } from './pages/transport-planning/transport-planning.component';
import { AddCustomerProjectModalComponent } from './pages/users/add-customer-project-modal/add-customer-project-modal.component';
import { LoginAsDirkUserComponent } from './pages/users/login-as-dirk-user/login-as-dirk-user.component';
import { LoginAsUserComponent } from './pages/users/login-as-user/login-as-user.component';
import { UserCustomerHistoryComponent } from './pages/users/user-customer-history/user-customer-history.component';
import { UserEditComponent } from './pages/users/user-edit/user-edit.component';
import { UserFormComponent } from './pages/users/user-form/user-form.component';
import { UserLoginInfoComponent } from './pages/users/user-login-info/user-login-info.component';
import { UserManagementComponent } from './pages/users/user-management/user-management.component';
import { UserRoleDescriptionsComponent } from './pages/users/user-role-descriptions/user-role-descriptions.component';
import { UserRoleHistoryComponent } from './pages/users/user-role-history/user-role-history.component';
import { UsersOverviewComponent } from './pages/users/users-overview/users-overview.component';
import { VkmChecklistOverviewComponent } from './pages/vkm/vkm-checklist-overview/vkm-checklist-overview.component';
import { VkmChecklistComponent } from './pages/vkm/vkm-checklist/vkm-checklist.component';
import { NameTabsPipe } from './pages/vkm/vkm-planning/name-tabs.pipe';
import { NotSettledCountPipe } from './pages/vkm/vkm-planning/not-settled-count.pipe';
import { SortTabsPipe } from './pages/vkm/vkm-planning/sort-tabs.pipe';
import { VkmAnswerPipe } from './pages/vkm/vkm-planning/vkm-answer.pipe';
import { VkmPlanningComponent } from './pages/vkm/vkm-planning/vkm-planning.component';
import { EditVkmVehicleDialogComponent } from './pages/vkm/vkm-projects/edit-vkm-vehicle-dialog/edit-vkm-vehicle-dialog.component';
import { MechanicSecondaryPipe } from './pages/vkm/vkm-projects/mechanic-secondary.pipe';
import { MechanicTypePipe } from './pages/vkm/vkm-projects/mechanic-type.pipe';
import { MechanicHasPlanningPipe } from './pages/vkm/vkm-projects/team-has-planning.pipe';
import { VkmDayPlanningComponent } from './pages/vkm/vkm-projects/vkm-day-planning.component';
import { VkmWorkordersComponent } from './pages/vkm/vkm-workorders/vkm-workorders.component';
import { WeekCheckRealisationsComponent } from './pages/week-check-realisations/week-check-realisations.component';
import { DayDetailComponent } from './pages/week-schedule/day-detail/day-detail.component';
import { RealisationDetailComponent } from './pages/week-schedule/realisation-detail/realisation-detail.component';
import { WeekScheduleDayComponent } from './pages/week-schedule/week-schedule-day/week-schedule-day.component';
import { WeekSchedulePlanningDetailComponent } from './pages/week-schedule/week-schedule-planning-detail/week-schedule-planning-detail.component';
import { WeekSchedulePlanningComponent } from './pages/week-schedule/week-schedule-planning/week-schedule-planning.component';
import { WeekScheduleRealisationsComponent } from './pages/week-schedule/week-schedule-realisations/week-schedule-realisations.component';
import { WeekScheduleComponent } from './pages/week-schedule/week-schedule.component';
import { AsphaltApplicationPipe } from './pipes/asphalt-application.pipe';
import { DateSubPipe } from './pipes/date-sub.pipe';
import { DatetimeBeforePipe } from './pipes/datetime-before.pipe';
import { DatetimeEqualsPipe } from './pipes/datetime-equals.pipe';
import { EntityTypePipe } from './pipes/entity-type.pipe';
import { EntityUnavailablePipe } from './pipes/entity-unavailable.pipe';
import { IsTodayPipe } from './pipes/is-today.pipe';
import { JoinByPipe } from './pipes/join-by.pipe';
import { MinutesDiffPipe } from './pipes/minutes-diff.pipe';
import { OrderdirectInvoiceStatusPipe } from './pipes/orderdirect-invoice-status.pipe';
import { OrderdirectPurchaseInvoiceStatusPipe } from './pipes/orderdirect-purchase-invoice-status.pipe';
import { PerformerOrHiringCompanyPipe } from './pipes/performer-or-hiring-company.pipe';
import { PhoneNumberPipe } from './pipes/phone-number.pipe';
import { RawMaterialQuantityPipe } from './pipes/raw-material-quantity.pipe';
import { RealisationAfasProjectIdPipe } from './pipes/realisation-afas-project-id.pipe';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { SafeResourceUrlPipe } from './pipes/safe-resource-url.pipe';
import { SearchPipe } from './pipes/search';
import { SourceLabelPipe } from './pipes/source-label.pipe';
import { SplitPipe } from './pipes/split.pipe';
import { EntitiesRowsPipe } from './pipes/transportplanning-entities-rows.pipe';
import { UserCanEditPlanningPipe } from './pipes/user-can-edit-planing.pipe';
import { UserDisplayNamePipe } from './pipes/user-display-name.pipe';
import { ValueInArrayPipe } from './pipes/value-in-array.pipe';
import { WeekTooltipPipe } from './pipes/week-tooltip.pipe';
import { WhereInPipe } from './pipes/where-in.pipe';
import { PlanningEntitiesToStringPipe } from './planning-entities-to-string.pipe';
import { ProjectInvoicesComponent } from './project-overview/project-invoices/project-invoices.component';
import { ReplaceIfPipe } from './replace-if.pipe';
import { SelectExecutorDialogComponent } from './select-executor-dialog/select-executor-dialog.component';
import { ApiService } from './services/api/api.service';
import { AsphaltService } from './services/asphalt/asphalt.service';
import { AuthService } from './services/auth/auth.service';
import { AuthorisationService } from './services/auth/authorisation.service';
import { ConfirmDialogService } from './services/confirm-dialog-service/confirm-dialog.service';
import { ConfirmDialogComponent } from './services/confirm-dialog-service/confirm-dialog/confirm-dialog.component';
import { DialogService } from './services/dialog/dialog.service';
import { LoadingDialogComponent } from './services/dialog/loading-dialog/loading-dialog.component';
import { PlanningDetailDialogService } from './services/dialog/planning-detail-dialog.service';
import { ExecutorPlanningService } from './services/executor-planning.service';
import { LocationService } from './services/location.service';
import { LoggerService } from './services/logger.service';
import { SettlementsService } from './services/settlements.service';
import { UserService } from './services/user/user.service';
import { WebsocketService } from './services/websocket/websocket.service';
import { ShiftDateRangeDialogComponent } from './shift-date-range-dialog/shift-date-range-dialog.component';
import { TooltipPipe } from './tooltip.pipe';
import { AddHiringToPlanningDialogComponent } from './trackline/pages/add-hiring-to-planning-dialog/add-hiring-to-planning-dialog.component';
import { OrderdirectInvoiceDetailComponent } from './trackline/pages/orderdirect-invoice-detail/orderdirect-invoice-detail.component';
import { OrderdirectInvoicesComponent } from './trackline/pages/orderdirect-invoices/orderdirect-invoices.component';
import { OrderdirectPurchaseInvoiceDetailComponent } from './trackline/pages/orderdirect-purchase-invoice-detail/orderdirect-purchase-invoice-detail.component';
import { OrderdirectPurchaseInvoicesComponent } from './trackline/pages/orderdirect-purchase-invoices/orderdirect-purchase-invoices.component';
import { TracklineDayReportPrintComponent } from './trackline/pages/trackline-day-report-print/trackline-day-report-print.component';
import { TracklineInventarisationOverviewComponent } from './trackline/pages/trackline-inventarisation-overview/trackline-inventarisation-overview.component';
import { TracklineOrderFormComponent } from './trackline/pages/trackline-order-form/trackline-order-form.component';
import { OrderTableFieldsPipe } from './trackline/pages/trackline-planning-edit-dialog/order-table-fields.pipe';
import { RelatedPlanningsComponent } from './trackline/pages/trackline-planning-edit-dialog/related-plannings/related-plannings.component';
import { TracklineEmployeePlanningComponent } from './trackline/pages/trackline-planning-edit-dialog/trackline-employee-planning/trackline-employee-planning.component';
import { TracklinePlanningEditDialogComponent } from './trackline/pages/trackline-planning-edit-dialog/trackline-planning-edit-dialog.component';
import { TracklinePlanningHasComponent } from './trackline/pages/trackline-planning-edit-dialog/trackline-planning-has/trackline-planning-has.component';
import { TracklinePlanningMaterialsComponent } from './trackline/pages/trackline-planning-edit-dialog/trackline-planning-materials/trackline-planning-materials.component';
import { TracklinePlanningEntityTypeDialogComponent } from './trackline/pages/trackline-planning-entity-type-dialog/trackline-planning-entity-type-dialog.component';
import { LocationFormatPipe } from './trackline/pages/trackline-planning/location-format.pipe';
import { NeedItemComponent } from './trackline/pages/trackline-planning/track-line-week/need-item/need-item.component';
import { PersonnelNamesPipe } from './trackline/pages/trackline-year-planning/personnel-names.pipe';
import { TracklineYearPlanningComponent } from './trackline/pages/trackline-year-planning/trackline-year-planning.component';
import { TransportplanningColsPipe } from './transportplanning-cols.pipe';

registerLocaleData(localeNL, 'nl');

export const myCustomTooltipDefaults: MatTooltipDefaultOptions = {
    showDelay: 750,
    hideDelay: 0,
    touchendHideDelay: 2000,
    disableTooltipInteractivity: true,
};

@Injectable()
export class FirstDayMondayDateAdapter extends NativeDateAdapter {
    getFirstDayOfWeek(): number {
        return 1; // Stel maandag in als de eerste dag van de week
    }
}

export function initializeAppFactory(authService: AuthorisationService): () => Promise<any> {
    return () => authService.rightsMap().toPromise();
}

declare var Hammer: any;

@Injectable()
export class MyHammerConfig extends HammerGestureConfig {
    override buildHammer(element: HTMLElement) {
        let mc = new Hammer(element, {
            touchAction: 'pan',
        });
        return mc;
    }
}

@NgModule({
    declarations: [
        AppComponent,
        UserEditComponent,
        LoginAsUserComponent,
        LoginAsDirkUserComponent,
        UsersOverviewComponent,
        OrderdirectInvoicesComponent,
        OrderdirectInvoiceDetailComponent,
        OrderdirectInvoiceStatusPipe,
        OrderdirectPurchaseInvoicesComponent,
        OrderdirectPurchaseInvoiceDetailComponent,
        OrderdirectPurchaseInvoiceStatusPipe,
        ConfirmDialogComponent,
        EntitiesOverviewComponent,
        EntityDetailComponent,
        PlanningOverviewComponent,
        PlanningDetailDialogComponent,
        CutterListComponent,
        WipetruckListComponent,
        AsphaltListComponent,
        AsphaltComponent,
        ImportAsphaltListDialogComponent,
        DayComponent,
        EntityComponent,
        TrucksComponent,
        TonsComponent,
        MaterialsManagement,
        MoveRealisationsToPerformerDialogComponent,
        ProjectInvoicesComponent,
        AsphaltOrderListComponent,
        SetPlanningFixedDialogComponent,
        AsphaltExportDialogComponent,
        PlanningListComponent,
        TooltipPipe,
        IsNightPipe,
        MaterialPiecePipe,
        JoinByPipe,
        RawMaterialQuantityPipe,
        IsSubsequentPipe,
        Nl2brPipe,
        UserCanEditPlanningPipe,
        PlanningEntitiesToStringPipe,
        AsphaltListItemComponent,
        PlanningExportDialogComponent,
        LoadingDialogComponent,
        PlanningDetailContainerComponent,
        PlanningDetailPrintComponent,
        HirePeopleSelectorDialogComponent,
        ProjectmanagerReportDialogComponent,
        HirePeopleDialogComponent,
        HiringCompaniesDialogComponent,
        SettlementsSendComponent,
        HiringCompanyEditDialogComponent,
        PlanningDetailEmployeesComponent,
        TransportPlanningComponent,
        TransportInUseComponent,
        MainPlanningPipe,
        ProfileComponent,
        ProfileAfasComponent,
        DatetimeInputComponent,
        WhereInPipe,
        RealisationAfasProjectIdPipe,
        HourEditDialogComponent,
        HourRegistrationComponent,
        WeekScheduleComponent,
        WeekScheduleDayComponent,
        WeekSchedulePlanningComponent,
        WeekScheduleRealisationsComponent,
        DayDetailComponent,
        RealisationDetailComponent,
        WeekSchedulePlanningDetailComponent,
        CheckHoursComponent,
        ProjectSelectionDialogComponent,
        PeriodSettlementComponent,
        RealisationForSettlementPipe,
        CumulativeSettlementAmountPipe,
        CumulativeSettlementMinutesPipe,
        TransportplanningColsPipe,
        EntityUnavailablePipe,
        HiredCompaniesDialogComponent,
        DayCheckRealisationsComponent,
        WeekCheckRealisationsComponent,
        AddressDialogComponent,
        AddressIconComponent,
        EquipmentDialogComponent,
        EntityTypePipe,
        CodaltComponent,
        MultiplePipe,
        AllApprovedPipe,
        CommentDialogComponent,
        HistoryDialogComponent,
        OwnHourEditAllowedPipe,
        EditSettlementDialogComponent,
        SplitPipe,
        LogoutComponent,
        DatetimeEqualsPipe,
        SimpleTransportTinyComponent,
        DatetimeEqualsPipe,
        LinkLoginComponent,
        RequestRestorePasswordComponent,
        RestorePasswordComponent,
        LicensePlatePipe,
        TransportAddressComponent,
        EmployeePlanningComponent,
        EmployeePlanningMakeKeyPipe,
        EmployeePlanningUserRowComponent,
        AddRealisationDialogComponent,
        EditRealisationProjectDialogComponent,
        SearchPipe,
        MinutesInputComponent,
        AddProjectDialogComponent,
        ProjectDayReportComponent,
        SettlementComponent,
        AsphaltUsedListComponent,
        AsphaltUsedListItemComponent,
        EntitiesRowsPipe,
        ProjectWeekReportConfigComponent,
        ProjectWeekReportComponent,
        HasLicensePlatePipe,
        ProjectDayHasAnyTeamPipe,
        DatetimeBeforePipe,
        ReplaceIfPipe,
        AsphaltApplicationPipe,
        EmployeeIsNightPipe,
        EntityIsNightPipe,
        CheckHoursFilterAnyPipe,
        HourEditTimeframePipe,
        TinyInsertImageComponent,
        CodaltUploadImageComponent,
        ProjectWeekReportHoursComponent,
        LeaveComponent,
        LeaveEditComponent,
        IsTodayPipe,
        TransportPlanningRowComponent,
        EditWeekCommentDialogComponent,
        WeekCommentHistoryDialogComponent,
        ExcludedKeyPipe,
        ToolboxDialogComponent,
        SafeResourceUrlPipe,
        ToolboxReadDialogComponent,
        WeekreportPdfOptionsDialogComponent,
        ToolboxReadDialogComponent,
        SettlementSendAllComponent,
        AddLeaveNameDialogComponent,
        EmployeeDayInfoDialogComponent,
        EmployeePlanningHelpComponent,
        LeaveRepeatDaysPipe,
        ProjectDetailDialogComponent,
        TransportPlanningTextComponent,
        ToolboxComponent,
        ToolboxEditComponent,
        GroupDayChangesPipe,
        ProjectsComponent,
        SafeHtmlPipe,
        HireNameComponent,
        VkmPlanningComponent,
        SortTabsPipe,
        NameTabsPipe,
        NotSettledCountPipe,
        VkmWorkordersComponent,
        VkmDayPlanningComponent,
        MechanicHasPlanningPipe,
        CopyRealisationFromOtherDayComponent,
        CopyStaffFromOtherDayComponent,
        MultipleDriverPipe,
        NotSendSettlementsComponent,
        PastToolboxesDialogComponent,
        EditSettlementProjectDialogComponent,
        VkmChecklistComponent,
        VkmChecklistOverviewComponent,
        MechanicTypePipe,
        MechanicSecondaryPipe,
        EditVkmVehicleDialogComponent,
        ProjectOverviewComponent,
        ToolboxQuestionFormComponent,
        QuestionComponent,
        QuizComponent,
        QuizStatsComponent,
        ToolboxQuizStatsComponent,
        VkmAnswerPipe,
        ProjectEditDialogComponent,
        RightsManagementComponent,
        RoleHasFunctionalityPipe,
        FunctionalityHasFeaturePipe,
        AfasFunctionsManagementComponent,
        FunctionHasRolePipe,
        IsRoleDisabledPipe,
        ProjectExecutorPlanningComponent,
        SelectItemDialogComponent,
        SelectProjectDialogComponent,
        ExecutorProjectChoiceDialogComponent,
        DuplicateEntrySelectDialogComponent,
        IsAsphaltEntryPipe,
        SelectExecutorDialogComponent,
        ShiftDateRangeDialogComponent,
        UserManagementComponent,
        UserRoleHistoryComponent,
        UserCustomerHistoryComponent,
        UserLoginInfoComponent,
        PlanningTableComponent,
        UserRoleDescriptionsComponent,
        WeekTooltipPipe,
        MinutesDiffPipe,
        MicrosoftCallbackComponent,
        TracklinePlanningEditDialogComponent,
        TracklinePlanningEntityTypeDialogComponent,
        TracklineEmployeePlanningComponent,
        TracklinePlanningHasComponent,
        TracklinePlanningMaterialsComponent,
        TracklinePlanningFilesComponent,
        RelatedPlanningsComponent,
        PlanningHistoryDialogComponent,
        SourceLabelPipe,
        TracklineInventarisationOverviewComponent,
        TracklineOrderFormComponent,
        TracklineDayReportPrintComponent,
        TracklineYearPlanningComponent,
        UserFormComponent,
        UserDisplayNamePipe,
        AddHiringToPlanningDialogComponent,
        StaffingComponent,
        AddCustomerProjectModalComponent,
        PdfBackgroundDialogComponent,
    ],
    imports: [
        DatepickerWeekNumberDirective,
        BrowserModule,
        HammerModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatButtonToggleModule,
        MatProgressSpinnerModule,
        MatProgressBarModule,
        MatSelectModule,
        MatDatepickerModule,
        NgSelectModule,
        MatNativeDateModule,
        MatRadioModule,
        MatAutocompleteModule,
        FormsModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatDividerModule,
        MatTooltipModule,
        MatSlideToggleModule,
        DragDropModule,
        ScrollingModule,
        MatIconModule,
        MatListModule,
        MatCheckboxModule,
        MatRippleModule,
        MatMenuModule,
        MatPaginatorModule,
        ClipboardModule,
        CdkMenuModule,
        MatSnackBarModule,
        AngularSvgIconModule.forRoot(),
        MatTabsModule,
        MatCardModule,
        GenericModule,
        NeedItemComponent,
        FieldInputModule,
        FieldsTemplateModule,
        MatExpansionModule,
        MatTableModule,
        MatBadgeModule,
        HttpClientModule,
        AiReportsModule,
        ActivityLetterPipe,
        ProjectStatusPipe,
        CdkPortalOutlet,
        OrderTableFieldsPipe,
        MatChipSet,
        MatChip,
        LocationFormatPipe,
        PmNamePipe,
        PersonnelNamesPipe,
        LocationSelectComponent,
        DateSubPipe,
        IsTraveltimePipe,
        DoesNotMatchDriverPipe,
        IsWorktimePipe,
        NotApprovablePipe,
        NotEditablePipe,
        ValueInArrayPipe,
        CheckHoursFilterPipe,
        DayReportModule,
        PerformerOrHiringCompanyPipe,
        PhoneNumberPipe,
    ],
    providers: [
        ApiService,
        AuthService,
        UserService,
        SettlementsService,
        AsphaltService,
        WebsocketService,
        DialogService,
        PlanningDetailDialogService,
        ConfirmDialogService,
        LocationService,
        AuthorisationService,
        DatePipe,
        IsSubsequentPipe,
        PlanningEntitiesToStringPipe,
        CdkDropList,
        CanDeactivateForm,
        LoggerService,
        ExecutorPlanningService,
        { provide: MAT_DATE_LOCALE, useValue: 'nl' },
        { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { closeOnNavigation: false } },
        { provide: DateAdapter, useClass: FirstDayMondayDateAdapter },
        { provide: ErrorHandler, useClass: CustomErrorHandlerService },
        { provide: LOCALE_ID, useValue: 'nl' },
        { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: myCustomTooltipDefaults },
        { provide: HTTP_INTERCEPTORS, useClass: CodaltInterceptor, multi: true },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeAppFactory,
            deps: [AuthorisationService],
            multi: true,
        },
        { provide: HAMMER_GESTURE_CONFIG, useClass: MyHammerConfig },
    ],
    exports: [],
    bootstrap: [AppComponent],
})
export class AppModule {
    constructor(private dateAdapter: DateAdapter<Date>) {
        this.dateAdapter.setLocale('nl'); // Gebruik de Nederlandse locale
    }
}
